import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import RefundModal from '../../components/RefundModal';
import { tierLabel } from '../../config/pricing';
import ThemeToggle from '../../components/ThemeToggle';

function MasterAdminFinancials() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [payments, setPayments] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [pastDueAccounts, setPastDueAccounts] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [filters, setFilters] = useState({
    tenantId: '',
    status: '',
    paymentType: '',
    startDate: '',
    endDate: ''
  });
  const [sortBy, setSortBy] = useState('payment_date');
  const [sortOrder, setSortOrder] = useState('DESC');

  // Refund dialog state. `refundPreview` holds the server's answer to "what
  // would this refund do?", so the confirmation shows server-computed figures
  // rather than the client's guess at them.
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundPreview, setRefundPreview] = useState(null);
  const [cancelTenant, setCancelTenant] = useState(false);

  const token = localStorage.getItem('masterAdminToken');
  const user = JSON.parse(localStorage.getItem('masterAdminUser') || '{}');

  const fetchPayments = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
        sortBy,
        sortOrder
      });

      if (filters.tenantId) params.append('tenantId', filters.tenantId);
      if (filters.status) params.append('status', filters.status);
      if (filters.paymentType) params.append('paymentType', filters.paymentType);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/master-admin/payments?${params}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error('Failed to fetch payments');
      const data = await response.json();
      setPayments(data.payments);
      setPagination(data.pagination);
    } catch (err) {
      console.error('Error fetching payments:', err);
    }
  }, [token, filters, sortBy, sortOrder, pagination.limit]);

  useEffect(() => {
    if (!token) {
      navigate('/master-admin/login');
      return;
    }

    const fetchData = async () => {
      try {
        // Fetch billing stats
        const billingResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/master-admin/billing`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!billingResponse.ok) throw new Error('Failed to fetch billing data');
        const billingData = await billingResponse.json();
        setStats(billingData.stats);
        setTenants(billingData.tenants);

        // Fetch past due accounts
        const pastDueResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/master-admin/past-due`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!pastDueResponse.ok) throw new Error('Failed to fetch past due accounts');
        const pastDueData = await pastDueResponse.json();
        setPastDueAccounts(pastDueData.pastDueAccounts);

        // Fetch payments
        await fetchPayments(1);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, navigate, fetchPayments]);

  const handleLogout = () => {
    localStorage.removeItem('masterAdminToken');
    localStorage.removeItem('masterAdminUser');
    navigate('/master-admin/login');
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    fetchPayments(1);
  };

  const clearFilters = () => {
    setFilters({
      tenantId: '',
      status: '',
      paymentType: '',
      startDate: '',
      endDate: ''
    });
    setTimeout(() => fetchPayments(1), 0);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('DESC');
    }
    setTimeout(() => fetchPayments(1), 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getTierLabel = (tier) => {
    const labels = {
      free_trial: 'Free Trial',
      starter: 'Starter',
      pro: 'Pro',
      enterprise: 'Enterprise'
    };
    return labels[tier] || tier;
  };

  // Ask the server what this refund would do before showing the dialog. The
  // amounts in the confirmation are the server's, not a client recomputation
  // that could disagree with what actually happens.
  const openRefund = async (payment) => {
    setRefundTarget(payment);
    setRefundPreview(null);
    setCancelTenant(false);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/master-admin/tenant-payments/${payment.id}/refund-preview`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Failed to load refund details');
      setRefundPreview(await response.json());
    } catch (err) {
      setError(err.message);
      setRefundTarget(null);
    }
  };

  const submitRefund = async ({ amount, reason }) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/master-admin/tenant-payments/${refundTarget.id}/refund`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          amount,
          reason,
          // Only sent when the admin explicitly ticked the box. A refund does
          // not imply cancellation.
          setTenantStatus: cancelTenant ? 'cancelled' : null
        })
      }
    );

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.details ? `${data.error}: ${data.details}` : data.error);
    }

    setRefundTarget(null);
    setRefundPreview(null);
    // Revenue figures and the payment list both change; reload from source
    // rather than patching local state and risking a stale total.
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="admin-page master-admin-page">
        <div className="admin-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="admin-page master-admin-page">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <h1>DeedStack Platform Admin</h1>
        </div>
        <div className="admin-header-right">
          <span className="admin-user-name">{user.firstName} {user.lastName}</span>
          <ThemeToggle />
          <button onClick={handleLogout} className="btn btn-secondary btn-small">
            Logout
          </button>
        </div>
      </header>

      {/* Navigation */}
      <nav className="admin-nav">
        <Link to="/master-admin/dashboard" className="admin-nav-link">Dashboard</Link>
        <Link to="/master-admin/tenants" className="admin-nav-link">Tenants</Link>
        <Link to="/master-admin/calendar" className="admin-nav-link">Calendar</Link>
        <Link to="/master-admin/emails" className="admin-nav-link">Emails</Link>
        <Link to="/master-admin/billing" className="admin-nav-link">Billing</Link>
        <Link to="/master-admin/financials" className="admin-nav-link active">Financials</Link>
        <Link to="/master-admin/settings" className="admin-nav-link">Settings</Link>
      </nav>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-container">
          {error && <div className="error-message">{error}</div>}

          {/* Revenue Dashboard */}
          <div className="admin-section">
            <h2>Revenue Dashboard</h2>
            {stats && (
              <>
                <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
                  <div className="stat-card">
                    <div className="stat-value">{formatCurrency(stats.mrr)}</div>
                    <div className="stat-label">Monthly Recurring Revenue</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{formatCurrency(stats.revenueThisMonth)}</div>
                    <div className="stat-label">Revenue This Month</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{formatCurrency(stats.revenueAllTime)}</div>
                    <div className="stat-label">Revenue All Time</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value" style={{ color: stats.pastDueCount > 0 ? '#dc3545' : 'inherit' }}>
                      {stats.pastDueCount}
                    </div>
                    <div className="stat-label">Past Due</div>
                  </div>
                </div>

                {/* Tier Breakdown */}
                <div className="admin-card" style={{ marginBottom: '2rem' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Active Subscribers by Tier</h3>
                  <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--color-text-light)' }}>
                        {stats.tierCounts?.free_trial || 0}
                      </div>
                      <div style={{ color: 'var(--color-text-light)', fontSize: '0.9rem' }}>Free Trial</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--color-success)' }}>
                        {stats.tierCounts?.starter || 0}
                      </div>
                      <div style={{ color: 'var(--color-text-light)', fontSize: '0.9rem' }}>{tierLabel('starter')}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#007bff' }}>
                        {stats.tierCounts?.pro || 0}
                      </div>
                      <div style={{ color: 'var(--color-text-light)', fontSize: '0.9rem' }}>{tierLabel('pro')}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#6f42c1' }}>
                        {stats.tierCounts?.enterprise || 0}
                      </div>
                      <div style={{ color: 'var(--color-text-light)', fontSize: '0.9rem' }}>{tierLabel('enterprise')}</div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Payment History */}
          <div className="admin-section">
            <h2>Payment History</h2>
            <div className="admin-card">
              {/* Filters */}
              <div style={{
                display: 'flex',
                gap: '1rem',
                flexWrap: 'wrap',
                marginBottom: '1rem',
                padding: '1rem',
                backgroundColor: 'var(--color-surface-sunken)',
                borderRadius: '8px'
              }}>
                <select
                  value={filters.tenantId}
                  onChange={(e) => handleFilterChange('tenantId', e.target.value)}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid var(--color-border)', fontSize: '16px', minHeight: '44px', boxSizing: 'border-box' }}
                >
                  <option value="">All Tenants</option>
                  {tenants.map(t => (
                    <option key={t.id} value={t.id}>{t.companyName}</option>
                  ))}
                </select>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid var(--color-border)', fontSize: '16px', minHeight: '44px', boxSizing: 'border-box' }}
                >
                  <option value="">All Statuses</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                  <option value="pending">Pending</option>
                </select>
                <select
                  value={filters.paymentType}
                  onChange={(e) => handleFilterChange('paymentType', e.target.value)}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid var(--color-border)', fontSize: '16px', minHeight: '44px', boxSizing: 'border-box' }}
                >
                  <option value="">All Types</option>
                  <option value="subscription">Subscription</option>
                  <option value="setup_fee">Setup Fee</option>
                  <option value="proration">Proration</option>
                  <option value="refund">Refund</option>
                </select>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid var(--color-border)', fontSize: '16px', minHeight: '44px', boxSizing: 'border-box' }}
                  placeholder="Start Date"
                />
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid var(--color-border)', fontSize: '16px', minHeight: '44px', boxSizing: 'border-box' }}
                  placeholder="End Date"
                />
                <button onClick={applyFilters} className="btn btn-primary btn-small">
                  Apply Filters
                </button>
                <button onClick={clearFilters} className="btn btn-secondary btn-small">
                  Clear
                </button>
              </div>

              {/* Payments Table */}
              <div style={{ overflowX: 'auto' }}>
                <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th
                        onClick={() => handleSort('payment_date')}
                        style={{
                          textAlign: 'left',
                          padding: '12px 8px',
                          borderBottom: '2px solid var(--color-border)',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}
                      >
                        Date {sortBy === 'payment_date' && (sortOrder === 'ASC' ? '↑' : '↓')}
                      </th>
                      <th
                        onClick={() => handleSort('company_name')}
                        style={{
                          textAlign: 'left',
                          padding: '12px 8px',
                          borderBottom: '2px solid var(--color-border)',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}
                      >
                        Tenant {sortBy === 'company_name' && (sortOrder === 'ASC' ? '↑' : '↓')}
                      </th>
                      <th
                        onClick={() => handleSort('amount')}
                        style={{
                          textAlign: 'right',
                          padding: '12px 8px',
                          borderBottom: '2px solid var(--color-border)',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}
                      >
                        Amount {sortBy === 'amount' && (sortOrder === 'ASC' ? '↑' : '↓')}
                      </th>
                      <th
                        onClick={() => handleSort('status')}
                        style={{
                          textAlign: 'center',
                          padding: '12px 8px',
                          borderBottom: '2px solid var(--color-border)',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}
                      >
                        Status {sortBy === 'status' && (sortOrder === 'ASC' ? '↑' : '↓')}
                      </th>
                      <th
                        onClick={() => handleSort('payment_type')}
                        style={{
                          textAlign: 'left',
                          padding: '12px 8px',
                          borderBottom: '2px solid var(--color-border)',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}
                      >
                        Type {sortBy === 'payment_type' && (sortOrder === 'ASC' ? '↑' : '↓')}
                      </th>
                      <th style={{
                        textAlign: 'right',
                        padding: '12px 8px',
                        borderBottom: '2px solid var(--color-border)'
                      }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map(payment => (
                      <tr key={payment.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                        <td style={{ padding: '12px 8px' }}>{formatDate(payment.paymentDate)}</td>
                        <td style={{ padding: '12px 8px' }}>
                          <Link
                            to={`/master-admin/tenants/${payment.tenantId}`}
                            style={{ color: '#007bff', textDecoration: 'none' }}
                          >
                            {payment.tenantName}
                          </Link>
                        </td>
                        <td style={{
                          padding: '12px 8px',
                          textAlign: 'right',
                          color: payment.isRefund ? '#dc3545' : 'inherit'
                        }}>
                          {/* Refund rows are stored negative, so the sign comes
                              from the value itself -- adding another '-' here
                              would render '--$69.00'. */}
                          {formatCurrency(payment.amount)}
                          {!payment.isRefund && payment.refundedAmount > 0 && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-error)' }}>
                              {formatCurrency(payment.refundedAmount)} refunded
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '0.85em',
                            backgroundColor: payment.status === 'completed' ? '#d4edda' :
                              payment.status === 'failed' ? '#f8d7da' : '#fff3cd',
                            color: payment.status === 'completed' ? '#155724' :
                              payment.status === 'failed' ? '#721c24' : '#856404'
                          }}>
                            {payment.status}
                          </span>
                        </td>
                        <td style={{ padding: '12px 8px', textTransform: 'capitalize' }}>
                          {payment.paymentType?.replace('_', ' ')}
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                          {payment.refundable ? (
                            <button
                              onClick={() => openRefund(payment)}
                              style={{
                                padding: '6px 10px',
                                fontSize: '0.8rem',
                                backgroundColor: '#dc3545',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              Refund
                            </button>
                          ) : (
                            <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                              {payment.isRefund
                                ? 'refund'
                                : payment.refundedAmount > 0
                                  ? 'fully refunded'
                                  : '-'}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {payments.length === 0 && (
                      <tr>
                        <td colSpan="6" style={{ padding: '24px', textAlign: 'center', color: 'var(--color-text-light)' }}>
                          No payments found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '1rem',
                  padding: '1rem 0'
                }}>
                  <div style={{ color: 'var(--color-text-light)' }}>
                    Showing {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => fetchPayments(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="btn btn-secondary btn-small"
                    >
                      Previous
                    </button>
                    <span style={{ padding: '8px 12px' }}>
                      Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => fetchPayments(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                      className="btn btn-secondary btn-small"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Past Due Accounts */}
          <div className="admin-section">
            <h2>Past Due Accounts</h2>
            <div className="admin-card">
              {pastDueAccounts.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: 'var(--color-success)' }}>
                  ✓ No past due accounts
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '2px solid var(--color-border)' }}>Company</th>
                        <th style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '2px solid var(--color-border)' }}>Tier</th>
                        <th style={{ textAlign: 'right', padding: '12px 8px', borderBottom: '2px solid var(--color-border)' }}>Amount Owed</th>
                        <th style={{ textAlign: 'center', padding: '12px 8px', borderBottom: '2px solid var(--color-border)' }}>Days Past Due</th>
                        <th style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '2px solid var(--color-border)' }}>Last Payment Attempt</th>
                        <th style={{ textAlign: 'center', padding: '12px 8px', borderBottom: '2px solid var(--color-border)' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pastDueAccounts.map(account => (
                        <tr key={account.id} style={{ borderBottom: '1px solid var(--color-border)', backgroundColor: 'var(--color-error-bg)' }}>
                          <td style={{ padding: '12px 8px' }}>
                            <div style={{ fontWeight: '600' }}>{account.companyName}</div>
                            <div style={{ fontSize: '0.85em', color: 'var(--color-text-light)' }}>
                              {account.subdomain}.deedstack.com
                            </div>
                          </td>
                          <td style={{ padding: '12px 8px' }}>{getTierLabel(account.subscriptionTier)}</td>
                          <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600', color: 'var(--color-error)' }}>
                            {formatCurrency(account.amountOwed)}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: '12px',
                              backgroundColor: account.daysPastDue > 30 ? '#dc3545' : '#ffc107',
                              color: account.daysPastDue > 30 ? 'white' : '#000',
                              fontWeight: '600'
                            }}>
                              {account.daysPastDue} days
                            </span>
                          </td>
                          <td style={{ padding: '12px 8px' }}>
                            {formatDate(account.lastPaymentAttempt)}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            <Link
                              to={`/master-admin/tenants/${account.id}`}
                              className="btn btn-primary btn-small"
                            >
                              View Details
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Only rendered once the server has returned the preview, so the dialog
          never shows a placeholder figure that a later response contradicts. */}
      {refundTarget && refundPreview && (
        <RefundModal
          isOpen
          onClose={() => { setRefundTarget(null); setRefundPreview(null); }}
          onSubmit={submitRefund}
          title={`Refund ${refundPreview.payment.companyName}`}
          maxRefundable={refundPreview.maxRefundable}
          alreadyRefunded={refundPreview.alreadyRefunded}
          methodLabel={
            refundPreview.canRefundViaSquare
              ? 'Issues a real refund through Square, and records it here.'
              : 'No Square payment to reverse. This records the refund on the books only.'
          }
          lines={[
            { label: 'Payment', value: `#${refundPreview.payment.id}` },
            { label: 'Date', value: formatDate(refundPreview.payment.paymentDate) },
            {
              label: 'Type',
              value: (refundPreview.payment.paymentType || '').replace('_', ' ')
            },
            {
              label: 'Original amount',
              value: formatCurrency(refundPreview.payment.amount),
              strong: true
            }
          ]}
          renderEffect={(amount) => (
            <>
              <div><strong>{formatCurrency(amount)}</strong> will be returned to {refundPreview.payment.companyName}.</div>
              <div style={{ marginTop: '4px' }}>
                Revenue This Month and Revenue All Time both drop by {formatCurrency(amount)}.
              </div>
              <div style={{ marginTop: '4px', color: 'var(--color-text-light)' }}>
                MRR is unchanged. It tracks the subscribed rate, not cash collected.
                {cancelTenant && ' Cancelling below is what removes them from MRR.'}
              </div>
            </>
          )}
          extraFields={
            <div style={{
              border: '1px solid var(--color-border)',
              borderRadius: '6px',
              padding: '10px 12px',
              marginBottom: '12px'
            }}>
              <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '0.85rem' }}>
                <input
                  type="checkbox"
                  checked={cancelTenant}
                  onChange={(e) => setCancelTenant(e.target.checked)}
                  style={{ marginTop: '3px' }}
                />
                <span>
                  Also set this tenant to <strong>Cancelled</strong>
                  <div style={{ color: 'var(--color-text-light)', marginTop: '2px' }}>
                    Current status: {refundPreview.tenant.subscriptionStatus}
                    {refundPreview.tenant.hasCardOnFile
                      ? ' · card on file'
                      : ' · no card on file'}
                    .
                    {refundPreview.tenant.hasCardOnFile && (
                      cancelTenant
                        ? ' Recurring billing will be stopped, so the card will not be charged again.'
                        : ' Leaving them active means recurring billing will charge that card again on the next cycle.'
                    )}
                  </div>
                </span>
              </label>
            </div>
          }
          warning={
            refundPreview.canRefundViaSquare
              ? 'This sends a real refund to Square. It cannot be undone from here.'
              : 'This payment did not go through Square. Recording a refund here corrects the books only. No money moves.'
          }
        />
      )}
    </div>
  );
}

export default MasterAdminFinancials;
